import { XCircleIcon } from "@heroicons/react/16/solid";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

export enum ModalVariant {
  DESTRUCTIVE = "destructive",
  CONSTRUCTIVE = "constructive",
}

interface ModalProps {
  title: string;
  onConfirm?: () => void;
  onConfirmLabel?: string;
  onConfirmDisabled?: boolean;
  onCancel?: () => void;
  onCancelLabel?: string;
  hideActionButtons?: boolean;
  isLoading?: boolean;
  confirmVariant?: ModalVariant;
  isWider?: boolean;
  openOnMount?: boolean;
  children?: ReactNode;
}

export interface UseModalReturn {
  Modal: React.ComponentType<ModalProps>;
  openModal: () => void;
}

export function useModal(): UseModalReturn {
  const { t } = useTranslation("commons");
  const modalId = useId();

  const handleConfirm = useCallback(
    (onConfirm: (() => void) | undefined, event: React.MouseEvent) => {
      event.stopPropagation();
      onConfirm?.();
      const modal = document.getElementById(modalId) as HTMLDialogElement;
      if (modal) {
        modal.close();
      }
    },
    [modalId],
  );

  const handleClose = useCallback(
    (onCancel: (() => void) | undefined, event: React.MouseEvent) => {
      event.stopPropagation();
      onCancel?.();
      const modal = document.getElementById(modalId) as HTMLDialogElement;
      if (modal) {
        modal.close();
      }
    },
    [modalId],
  );

  const openModal = useCallback(() => {
    const modal = document.getElementById(modalId) as HTMLDialogElement;
    if (modal) {
      modal.showModal();
    }
  }, [modalId]);

  const ModalComponent = useMemo(
    () =>
      React.memo<ModalProps>((props) => {
        const { children, ...modalProps } = props;

        useEffect(() => {
          if (modalProps.openOnMount) {
            openModal();
          }
        }, [modalProps.openOnMount]);

        const confirmButtonClass = useMemo(
          () =>
            modalProps.confirmVariant === ModalVariant.DESTRUCTIVE
              ? "btn btn-error"
              : "btn btn-primary",
          [modalProps.confirmVariant],
        );

        return createPortal(
          <dialog
            id={modalId}
            className="modal"
            aria-labelledby={`${modalId}-title`}
            aria-modal="true"
          >
            <div
              className={`modal-box max-h-[90vh] overflow-clip p-3 sm:p-5 ${modalProps.isWider ? "max-w-3xl" : ""}`}
            >
              <div className="flex min-h-[2rem] w-full justify-between">
                <h3
                  id={`${modalId}-title`}
                  className="font-bold text-l sm:text-xl"
                >
                  {modalProps.title}
                </h3>
                <div className="flex justify-end">
                  <form method="dialog">
                    <button
                      className="btn btn-circle btn-ghost absolute top-1 right-1 sm:top-2 sm:right-2"
                      type="button"
                      onClick={(e) => handleClose(modalProps.onCancel, e)}
                    >
                      <XCircleIcon className="h-5 w-5 sm:h-7 sm:w-7" />
                    </button>
                  </form>
                </div>
              </div>
              <div className="modal-content">
                <div className="max-h-[calc(90vh-160px)] overflow-y-auto">
                  {children ? (
                    children
                  ) : modalProps.isLoading ? (
                    <div className="flex w-full items-center justify-center py-4">
                      <span className="loading loading-spinner loading-xs" />
                    </div>
                  ) : null}
                </div>
                {!modalProps.hideActionButtons &&
                  (modalProps.onConfirm || modalProps.onCancel) && (
                    <div className="modal-action mt-4">
                      {(modalProps.onConfirm || modalProps.onConfirmLabel) && (
                        <button
                          className={`btn ${confirmButtonClass}`}
                          disabled={modalProps.onConfirmDisabled}
                          onClick={(e) =>
                            handleConfirm(modalProps.onConfirm, e)
                          }
                          type="button"
                        >
                          {modalProps.onConfirmLabel || t("confirm")}
                        </button>
                      )}
                      {(modalProps.onCancel || modalProps.onCancelLabel) && (
                        <button
                          className="btn btn-neutral"
                          onClick={(e) => handleClose(modalProps.onCancel, e)}
                          type="button"
                        >
                          {modalProps.onCancelLabel || t("cancel")}
                        </button>
                      )}
                    </div>
                  )}
              </div>
            </div>
          </dialog>,
          document.body,
        );
      }),
    [modalId, handleConfirm, handleClose, t, openModal],
  );

  return useMemo(
    () => ({
      Modal: ModalComponent,
      openModal,
    }),
    [ModalComponent, openModal],
  );
}
